import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTranslation } from 'react-i18next';
import Legend from '../Legend';
import AreaGradient from '../Common/Charts/LineChartAreaGradient';
import { DashboardCard } from '../Common/SurfaceCard';

interface DailyEntriesProps {
    entriesPerDayXValues: number[];
    entriesPerDayYValues: {
        thisMonth: number[];
        lastMonth: number[];
        lastYear: number[];
    };
}

const ChartContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: ${({ theme }) => theme.spacing(30)};
    ${({ theme }) => theme.breakpoints.up('md')} {
        min-height: 0;
    }
`;

export default function DailyEntries({ entriesPerDayXValues, entriesPerDayYValues }: DailyEntriesProps) {
    const { t } = useTranslation("common");
    const theme = useTheme();

    const colorPalette = [
        theme.palette.primary.light,
        theme.palette.primary.main,
        theme.palette.primary.dark,
    ];

    return (
        <DashboardCard title={t("entries_per_day_title")}>
            <Legend
                items={[
                    { color: theme.palette.primary.light, label: t("linechart_days_set1") },
                    { color: theme.palette.primary.main, label: t("linechart_days_set2") },
                    { color: theme.palette.primary.dark, label: t("linechart_days_set3") },
                ]}
            />
            <ChartContainer>
                <LineChart
                    colors={colorPalette}
                    xAxis={[
                        {
                            scaleType: 'point',
                            data: entriesPerDayXValues,
                        },
                    ]}
                    series={[
                        {
                            id: 'thisMonth',
                            label: t("linechart_days_set1"),
                            showMark: false,
                            curve: 'linear',
                            area: true,
                            data: entriesPerDayYValues.thisMonth,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                        {
                            id: 'lastMonth',
                            label: t("linechart_days_set2"),
                            showMark: false,
                            curve: 'linear',
                            data: entriesPerDayYValues.lastMonth,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                        {
                            id: 'lastYear',
                            label: t("linechart_days_set3"),
                            showMark: false,
                            curve: 'linear',
                            data: entriesPerDayYValues.lastYear,
                            valueFormatter: value => `${value || 0} ${t("cars")}`,
                        },
                    ]}
                    margin={{ left: 50, right: 20, top: 20, bottom: 20 }}
                    grid={{ horizontal: true, vertical: true }}
                    sx={{
                        '& .MuiAreaElement-series-thisMonth': { fill: "url('#thisMonth')" },
                        '& .MuiAreaElement-series-lastMonth': { fill: "url('#lastMonth')" },
                        '& .MuiAreaElement-series-lastYear': { fill: "url('#lastYear')" },
                    }}
                    slotProps={{ legend: { hidden: true } }}
                >
                    <AreaGradient color={theme.palette.primary.dark} id="lastYear" />
                    <AreaGradient color={theme.palette.primary.main} id="lastMonth" />
                    <AreaGradient color={theme.palette.primary.light} id="thisMonth" />
                </LineChart>
            </ChartContainer>
        </DashboardCard>
    );
}
